import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Shape of GET /territories/{insee}/solar.
 * Matches the dict returned by `get_territory_solar_summary` in
 * backend/app/services/solar_cadastre.py.
 */
export interface SolarData {
  /** Estimated total solar production potential in GWh/year. */
  potential_gwh: number;
  /** Percentage of buildings with a favorable roof orientation (S, SE, SW). */
  favorable_pct: number;
  /** CO₂ avoided in tonnes/year (FR electricity mix — 0.0556 kg CO₂/kWh). */
  co2_avoided_t: number;
  /** Monthly production profile — exactly 12 values (Jan → Dec), in GWh. */
  monthly_gwh: number[];
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetches solar cadastre summary data for a commune (INSEE code).
 * GET /territories/{insee}/solar
 *
 * Returns the standard React Query result (data, isLoading, error, refetch…).
 * Query is disabled while `insee` is null (no territory selected).
 */
export function useSolar(insee: string | null) {
  return useQuery<SolarData>({
    queryKey: ['solar', insee],
    queryFn: async () => {
      const { data } = await api.get<SolarData>(`/territories/${insee}/solar`);
      return data;
    },
    enabled: !!insee,
  });
}
